import { SetURLSearchParams } from "react-router-dom";

/**
 * The By Brand tab's `tab` value. It lives here because the brands modals on
 * the other tabs link straight into it, carrying a `brand` id with them.
 */
export const FETCHER_BY_BRAND_TAB = "by_brand";

/** Rows-per-page choices every Fetcher Settings table offers. */
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 100];

export const DEFAULT_ROWS_PER_PAGE = ROWS_PER_PAGE_OPTIONS[0];

/**
 * Readers and writers for the query params the Fetcher Settings tabs share.
 *
 * The URL is the single source of truth for the search term and pagination:
 * `FilterComponent` writes the filter selections (and resets `page`) itself, so
 * a tab that also kept its own copy of `page` in state would drift out of step
 * with it. Each tab therefore reads these straight off `searchParams`, the same
 * way the other FilterComponent-paired tables in the app do.
 *
 * The readers all have to treat an absent param as "use the default", which is
 * easy to get wrong: `Number(null)` is `0`, not `NaN`, so a naive
 * `Number.isInteger(parsed) && parsed >= 0` check accepts a missing param as 0.
 * That is what silently made `page_size` 0 - and every table render no rows -
 * until the user picked a rows-per-page value by hand.
 */
const readInt = (value: string | null): number | null => {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
};

/** 0-based page index; anything negative or unparseable falls back to 0. */
export function readPageParam(value: string | null): number {
  const parsed = readInt(value);
  return parsed !== null && parsed >= 0 ? parsed : 0;
}

/**
 * Rows per page, restricted to the values the footer actually offers, so a
 * stale or hand-edited `?page_size=0` cannot empty the table.
 */
export function readRowsPerPageParam(value: string | null): number {
  const parsed = readInt(value);
  return parsed !== null && ROWS_PER_PAGE_OPTIONS.includes(parsed)
    ? parsed
    : DEFAULT_ROWS_PER_PAGE;
}

/** The exact value picked in the tab's search box, or null when it is empty. */
export function readSearchParam(value: string | null): string | null {
  return value && value.trim() !== "" ? value : null;
}

const writeParams = (
  setSearchParams: SetURLSearchParams,
  apply: (next: URLSearchParams) => void,
) =>
  setSearchParams(
    (previous) => {
      // built from `previous` so the tab's other params - and anything
      // FilterComponent put there - survive untouched
      const next = new URLSearchParams(previous);
      apply(next);
      return next;
    },
    { replace: true },
  );

export function writePageParam(
  setSearchParams: SetURLSearchParams,
  page: number,
) {
  writeParams(setSearchParams, (next) => {
    if (page > 0) next.set("page", String(page));
    else next.delete("page");
  });
}

/** Changing the page size re-flows the rows, so the page goes back to the first. */
export function writeRowsPerPageParam(
  setSearchParams: SetURLSearchParams,
  rowsPerPage: number,
) {
  writeParams(setSearchParams, (next) => {
    if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) {
      next.set("page_size", String(rowsPerPage));
    } else {
      next.delete("page_size");
    }
    next.delete("page");
  });
}

/** A narrower list can be shorter than the page the user is on, so page resets. */
export function writeSearchParam(
  setSearchParams: SetURLSearchParams,
  value: string | null,
) {
  writeParams(setSearchParams, (next) => {
    if (value) next.set("search", value);
    else next.delete("search");
    next.delete("page");
  });
}
